import React, { useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { DreamscapeProvider } from './contexts/DreamscapeContext';
import { PWAProvider, registerServiceWorker } from './hooks/usePWA';
import { InstallBanner, IOSInstallModal } from './components/PWA';
import UpdateBanner from './components/UpdateBanner';
import ProtectedRoute from './components/ProtectedRoute';
import RootRedirect from './components/RootRedirect';
import Login from './pages/Login';
import Signup from './pages/Signup';
import SignupMethod from './pages/SignupMethod';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import Dashboard from './pages/Dashboard';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminMetrics from './pages/admin/AdminMetrics';
import AdminInvites from './pages/admin/AdminInvites';
import Credits from './pages/Credits';
import Subscription from './pages/Subscription';
import BillingHistory from './pages/BillingHistory';
import ChangePassword from './pages/settings/ChangePassword';
import ChangeUsername from './pages/settings/ChangeUsername';
import ChangeLanguage from './pages/settings/ChangeLanguage';
import NotificationSettings from './pages/settings/NotificationSettings';
import ThemeSettings from './pages/settings/ThemeSettings';
import WriterProfileSettings from './pages/settings/WriterProfile';
import AuthorDetailsSettings from './pages/settings/AuthorDetails';
import AiModelSettings from './pages/settings/AiModel';
import ApiAccessSettings from './pages/settings/ApiAccess';
import Onboarding from './pages/onboarding/Onboarding';
import StoryDashboard from './pages/story/StoryDashboard';
import CreateStory from './pages/story/CreateStory';
import StoryWorkspace from './pages/story/StoryWorkspace';
import ChapterEditor from './pages/story/ChapterEditor';
import AcceptInvite from './pages/story/AcceptInvite';
import AcceptPlanInvite from './pages/AcceptPlanInvite';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import Guidelines from './pages/legal/Guidelines';
import GoogleSuccess from './pages/auth/GoogleSuccess';
import OAuthAuthorize from './pages/auth/OAuthAuthorize';

// Layout wrapper component (without providers)
const Layout = () => {
  useEffect(() => {
    // Register service worker on mount
    registerServiceWorker().catch(console.error);
  }, []);

  return (
    <div className="h-full">
      <Toaster
        position="top-center"
        toastOptions={{
          className: '',
          style: {
            background: 'var(--toast-bg, #1a1a1a)',
            color: 'var(--toast-color, #fff)',
            border: '1px solid var(--toast-border, #2a2a2a)',
          },
        }}
      />
      <UpdateBanner />
      <InstallBanner />
      <IOSInstallModal />
      <Outlet />
    </div>
  );
};

// Create router using React Router v7 syntax
const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <Layout />,
      children: [
        { index: true, element: <RootRedirect /> },
        { path: 'login', element: <Login /> },
        { path: 'signup', element: <SignupMethod /> },
        { path: 'signup/email', element: <Signup /> },
        { path: 'verify-email', element: <VerifyEmail /> },
        { path: 'forgot-password', element: <ForgotPassword /> },
        { path: 'auth/google/success', element: <GoogleSuccess /> },
        { path: 'terms', element: <Terms /> },
        { path: 'privacy', element: <Privacy /> },
        // Signed-out visitors see a sign-in prompt; the page remembers the link for after.
        { path: 'invite/:token', element: <AcceptInvite /> },
        // An emailed invite to a complimentary plan; same signed-out handling.
        { path: 'join/:token', element: <AcceptPlanInvite /> },
        // An MCP connector (claude.ai, ChatGPT) asking to connect: the OAuth consent page.
        { path: 'oauth/authorize', element: <OAuthAuthorize /> },
        {
          element: <ProtectedRoute />,
          children: [
            { path: 'onboarding', element: <Onboarding /> },
            { path: 'onboarding/:step', element: <Onboarding /> },
            { path: 'dashboard', element: <Dashboard /> },
            // Legacy companion/chat deep links (old emails, push notifications,
            // bookmarks) land in the story library.
            { path: 'chat/*', element: <Navigate to="/stories" replace /> },
            { path: 'companions', element: <Navigate to="/stories" replace /> },
            { path: 'companion/*', element: <Navigate to="/stories" replace /> },
            { path: 'settings/companion/*', element: <Navigate to="/profile" replace /> },
            { path: 'settings', element: <Settings /> },
            { path: 'settings/password', element: <ChangePassword /> },
            { path: 'settings/username', element: <ChangeUsername /> },
            { path: 'settings/language', element: <ChangeLanguage /> },
            { path: 'settings/notifications', element: <NotificationSettings /> },
            { path: 'settings/theme', element: <ThemeSettings /> },
            { path: 'settings/writer-profile', element: <WriterProfileSettings /> },
            { path: 'settings/author-details', element: <AuthorDetailsSettings /> },
            { path: 'settings/ai-model', element: <AiModelSettings /> },
            { path: 'settings/api', element: <ApiAccessSettings /> },
            { path: 'profile', element: <Profile /> },
            { path: 'guidelines', element: <Guidelines /> },
            { path: 'credits', element: <Credits /> },
            { path: 'subscription', element: <Subscription /> },
            { path: 'billing', element: <BillingHistory /> },
            { path: 'stories', element: <StoryDashboard /> },
            { path: 'stories/new', element: <CreateStory /> },
            { path: 'stories/:id', element: <StoryWorkspace /> },
            { path: 'stories/:id/chapters/:chapterId', element: <ChapterEditor /> },
            { path: 'admin', element: <AdminDashboard /> },
            { path: 'admin/users', element: <AdminUsers /> },
            { path: 'admin/users/:userId', element: <AdminUserDetail /> },
            { path: 'admin/metrics', element: <AdminMetrics /> },
            { path: 'admin/invites', element: <AdminInvites /> },
          ],
        },
        { path: '*', element: <Navigate to="/" replace /> },
      ],
    },
  ],
  {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);

function App() {
  return (
    <DreamscapeProvider>
      <AuthProvider>
        <ThemeProvider>
          <PWAProvider>
            <RouterProvider router={router} />
          </PWAProvider>
        </ThemeProvider>
      </AuthProvider>
    </DreamscapeProvider>
  );
}

export default App;
